import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Profile, Video, WatchHistoryEntry } from '@kidstube/shared';
import type { ParentSettings, ListEntry, SettingsExport } from '@kidstube/shared';

/**
 * TIMER SYSTEM - WALL CLOCK BASED
 * 
 * How it works:
 * 1. Parent sets duration (e.g., 15 minutes) → we store startedAt timestamp + duration
 * 2. Remaining time = (startedAt + duration) - now
 * 3. No tracking of "watched seconds" - pure wall clock
 * 4. Timer NEVER resets unless parent explicitly resets
 */

interface AppState {
    /** Current Google authentication identity; it does not own family data. */
    accountId: string | null;
    /** Prevent stale local state from being uploaded before D1 has been read. */
    isCloudHydrated: boolean;
    /** Idempotency journal for a legacy local-history upload that was interrupted. */
    migratedWatchKeys: string[];
    currentProfile: Profile | null;
    profiles: Profile[];
    // Timer state - ONLY these two values matter
    sessionStartedAt: string | null;      // ISO timestamp when parent set timer
    sessionDurationMinutes: number | null; // Duration parent selected
    parentSettings: ParentSettings | null;
    allowlist: ListEntry[];
    denylist: ListEntry[];
    watchHistory: WatchHistoryEntry[];
    discoveredVideos: Video[];
    /** One-query local result cache. A different query explicitly replaces it. */
    searchCache: { queryKey: string; videos: Video[]; cachedAt: string } | null;
    durationHistory: number[];  // LIFO array of recent durations
    playbackResume: { videoId: string; seconds: number; updatedAt: string } | null;
    isFullscreen: boolean;
    isSyncing: boolean;  // Cloud sync in progress
    lastSyncedAt: string | null;  // Last successful cloud sync

    // Actions
    setCurrentProfile: (profile: Profile) => void;
    startSession: (limitMinutes: number) => void;
    resetSession: () => void;
    extendSession: (additionalMinutes: number) => void;
    unlockWithPin: (pin: string) => Promise<boolean>;
    toggleFullscreen: () => void;
    addToWatchHistory: (entry: Omit<WatchHistoryEntry, 'watchedAt'>) => void;
    updateWatchHistoryDuration: (videoId: string, secondsToAdd: number, totalDurationSeconds?: number) => void;
    addDiscoveredVideos: (videos: Video[]) => void;
    cacheSearchResults: (queryKey: string, videos: Video[]) => void;
    clearSearchCache: () => void;
    importSettings: (data: SettingsExport) => void;
    addToAllowlist: (entry: ListEntry) => void;
    addToDenylist: (entry: ListEntry) => void;
    removeFromAllowlist: (id: string) => void;
    removeFromDenylist: (id: string) => void;
    savePlaybackPosition: (videoId: string, seconds: number, totalDurationSeconds?: number) => void;
    clearPlaybackPosition: (videoId: string) => void;

    // Cloud sync actions (uses httpOnly session cookie)
    activateAccount: (accountId: string) => boolean;
    clearAccountData: () => void;
    syncToCloud: (includePreferences?: boolean) => Promise<boolean>;
    fetchFromCloud: (expectedAccountId?: string) => Promise<boolean>;
    fetchWatchHistory: (profileId?: string, expectedAccountId?: string) => Promise<boolean>;
    migrateExistingWatchState: (expectedAccountId?: string) => Promise<boolean>;

    // Computed (call these to get current state based on wall clock)
    getRemainingSeconds: () => number;
    isTimeUp: () => boolean;
    hasActiveSession: () => boolean;
}

const DEFAULT_PROFILE: Profile = {
    id: 'default',
    name: 'Kid',
    avatarId: '👶',
    sessionTimeLimitMinutes: 60,
    createdAt: new Date().toISOString()
};

const DISCOVERED_VIDEO_LIMIT = 100;
const WATCH_HISTORY_LIMIT = 200;

interface CloudWatchHistoryItem {
    profileId?: string;
    videoId?: string;
    videoTitle?: string;
    channelId?: string;
    channelTitle?: string;
    thumbnailUrl?: string;
    watchedDurationSeconds?: number;
    totalDurationSeconds?: number;
    lastPositionSeconds?: number;
    watchedAt?: string;
    updatedAt?: string;
}

async function hashPin(pin: string, salt: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(pin + salt);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function normalizeSeconds(value: number | undefined): number {
    return Number.isFinite(value) ? Math.max(0, Math.floor(value || 0)) : 0;
}

function mapCloudWatchHistory(items: CloudWatchHistoryItem[] | undefined, fallbackProfileId = DEFAULT_PROFILE.id): WatchHistoryEntry[] {
    if (!items) return [];

    return items
        .filter(item => typeof item.videoId === 'string' && item.videoId.length > 0)
        .map(item => ({
            profileId: item.profileId || fallbackProfileId,
            videoId: item.videoId || '',
            videoTitle: item.videoTitle || 'Unknown video',
            channelId: item.channelId || undefined,
            channelTitle: item.channelTitle || '',
            thumbnailUrl: item.thumbnailUrl || '',
            watchedAt: item.watchedAt || item.updatedAt || new Date().toISOString(),
            watchedDurationSeconds: normalizeSeconds(item.watchedDurationSeconds),
            totalDurationSeconds: normalizeSeconds(item.totalDurationSeconds),
            lastPositionSeconds: normalizeSeconds(item.lastPositionSeconds)
        }));
}

function mergeWatchHistoryEntry(existing: WatchHistoryEntry, incoming: WatchHistoryEntry): WatchHistoryEntry {
    const existingTime = Date.parse(existing.watchedAt);
    const incomingTime = Date.parse(incoming.watchedAt);
    const incomingIsNewer = !Number.isFinite(existingTime) || (Number.isFinite(incomingTime) && incomingTime >= existingTime);
    const newerEntry = incomingIsNewer ? incoming : existing;
    const olderEntry = incomingIsNewer ? existing : incoming;

    return {
        ...olderEntry,
        ...newerEntry,
        watchedDurationSeconds: Math.max(existing.watchedDurationSeconds || 0, incoming.watchedDurationSeconds || 0),
        totalDurationSeconds: Math.max(existing.totalDurationSeconds || 0, incoming.totalDurationSeconds || 0),
        lastPositionSeconds: normalizeSeconds(
            newerEntry.lastPositionSeconds !== undefined
                ? newerEntry.lastPositionSeconds
                : olderEntry.lastPositionSeconds
        )
    };
}

function mergeWatchHistoryEntries(existing: WatchHistoryEntry[], incoming: WatchHistoryEntry[]): WatchHistoryEntry[] {
    const byVideoId = new Map<string, WatchHistoryEntry>();

    for (const entry of [...existing, ...incoming]) {
        if (!entry.videoId) continue;
        const key = `${entry.profileId || DEFAULT_PROFILE.id}:${entry.videoId}`;
        const current = byVideoId.get(key);
        byVideoId.set(key, current ? mergeWatchHistoryEntry(current, entry) : entry);
    }

    return [...byVideoId.values()]
        .sort((a, b) => {
            const aTime = Date.parse(a.watchedAt);
            const bTime = Date.parse(b.watchedAt);
            return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
        })
        .slice(0, WATCH_HISTORY_LIMIT);
}

function isBlockedEntry(entry: { id?: string; type?: string; channelId?: string; videoId?: string }, denylist: ListEntry[]): boolean {
    return denylist.some(denied =>
        (denied.type === 'video' && (entry.videoId === denied.id || entry.id === denied.id))
        || (denied.type === 'channel' && entry.channelId === denied.id)
    );
}

function isBlockedVideo(video: Video, denylist: ListEntry[]): boolean {
    return isBlockedEntry({ videoId: video.id, channelId: video.snippet.channelId }, denylist);
}

export const useAppStore = create<AppState>()(
    persist(
        (set, get) => ({
            accountId: null,
            isCloudHydrated: false,
            migratedWatchKeys: [],
            currentProfile: null,
            profiles: [DEFAULT_PROFILE],
            sessionStartedAt: null,
            sessionDurationMinutes: null,
            parentSettings: null,
            allowlist: [],
            denylist: [],
            watchHistory: [],
            discoveredVideos: [],
            searchCache: null,
            durationHistory: [15, 30, 45, 60],  // Default options
            playbackResume: null,
            isFullscreen: false,
            isSyncing: false,
            lastSyncedAt: null,

            activateAccount: (accountId: string) => {
                const state = get();
                if (state.accountId === accountId) {
                    set({ isCloudHydrated: false });
                    return false;
                }

                // Google identifies the authenticated request only. A different
                // signed-in account must hydrate the same shared family D1 scope.
                if (state.accountId) {
                    set({
                        accountId,
                        isCloudHydrated: false,
                        isSyncing: false,
                        lastSyncedAt: null
                    });
                    return false;
                }

                set({ accountId, isCloudHydrated: false });
                return true;
            },

            clearAccountData: () => {
                set({
                    accountId: null,
                    isCloudHydrated: false,
                    migratedWatchKeys: [],
                    currentProfile: null,
                    profiles: [DEFAULT_PROFILE],
                    sessionStartedAt: null,
                    sessionDurationMinutes: null,
                    parentSettings: null,
                    allowlist: [],
                    denylist: [],
                    watchHistory: [],
                    discoveredVideos: [],
                    searchCache: null,
                    durationHistory: [15, 30, 45, 60],
                    playbackResume: null,
                    isFullscreen: false,
                    isSyncing: false,
                    lastSyncedAt: null
                });
            },

            setCurrentProfile: (profile: Profile) => {
                set({ currentProfile: profile });
                queueMicrotask(() => {
                    const state = get();
                    if (!state.accountId || !state.isCloudHydrated) return;
                    void state.syncToCloud().then(() => state.fetchWatchHistory(profile.id));
                });
            },

            // Cloud sync: Push local state to server (merge, not replace)
            syncToCloud: async (includePreferences = false) => {
                const state = get();
                const accountId = state.accountId;
                if (!accountId || !state.isCloudHydrated) return false;
                set({ isSyncing: true });
                try {
                    const response = await fetch('/api/sync', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({
                            action: includePreferences ? 'full_sync' : 'save_state',
                            ...(includePreferences ? {
                                allowlist: state.allowlist,
                                denylist: state.denylist
                            } : {}),
                            profiles: state.profiles,
                            currentProfileId: state.currentProfile?.id || null,
                            parentSettings: state.parentSettings,
                            sessionStartedAt: state.sessionStartedAt,
                            sessionDurationMinutes: state.sessionDurationMinutes,
                            durationHistory: state.durationHistory
                        })
                    });
                    if (response.ok && get().accountId === accountId) {
                        set({ lastSyncedAt: new Date().toISOString(), isSyncing: false });
                        return true;
                    }
                    if (get().accountId === accountId) set({ isSyncing: false });
                    return false;
                } catch (e) {
                    if (get().accountId === accountId) set({ isSyncing: false });
                    return false;
                }
            },

            // Cloud sync: D1 wins once the account has an existing cloud state.
            fetchFromCloud: async (expectedAccountId?: string) => {
                const accountId = expectedAccountId || get().accountId;
                if (!accountId || get().accountId !== accountId) return false;

                const wasHydrated = get().isCloudHydrated;
                // Initial hydration gates family data. Later polls are silent and
                // must not unmount the Parent Dashboard or relock its PIN screen.
                set({ isSyncing: true, ...(wasHydrated ? {} : { isCloudHydrated: false }) });
                try {
                    const response = await fetch('/api/sync', {
                        method: 'GET',
                        credentials: 'include'
                    });
                    if (!response.ok || get().accountId !== accountId) {
                        if (get().accountId === accountId) set({ isSyncing: false });
                        return false;
                    }

                    const data = await response.json();
                    if (!data.hasCloudState) {
                        // The only time browser data can seed D1 is the family's first
                        // cloud state. Upload history/resume first so a successful state
                        // write is not allowed to hide an incomplete migration.
                        const migrated = await get().migrateExistingWatchState(accountId);
                        if (!migrated || get().accountId !== accountId) {
                            if (get().accountId === accountId) set({ isSyncing: false });
                            return false;
                        }

                        set({ isCloudHydrated: true, isSyncing: false });
                        const synced = await get().syncToCloud(true);
                        if (synced && get().accountId === accountId) {
                            set({ migratedWatchKeys: [] });
                        }
                        return synced;
                    }

                    const latestState = get();
                    const cloudProfiles = Array.isArray(data.profiles) && data.profiles.length > 0
                        ? data.profiles as Profile[]
                        : [DEFAULT_PROFILE];
                    const cloudCurrentProfile = cloudProfiles.find(profile => profile.id === data.currentProfileId)
                        || cloudProfiles[0]
                        || DEFAULT_PROFILE;
                    const cloudDenylist = (data.denylist || []) as ListEntry[];
                    const safeDiscoveredVideos = latestState.discoveredVideos.filter(video => !isBlockedVideo(video, cloudDenylist));
                    const safeSearchCache = latestState.searchCache
                        ? {
                            ...latestState.searchCache,
                            videos: latestState.searchCache.videos.filter(video => !isBlockedVideo(video, cloudDenylist))
                        }
                        : null;
                    const safeLocalHistory = latestState.watchHistory.filter(entry => !isBlockedEntry(entry, cloudDenylist));

                    set({
                        allowlist: data.allowlist || [],
                        denylist: cloudDenylist,
                        profiles: cloudProfiles,
                        currentProfile: cloudCurrentProfile,
                        parentSettings: data.parentSettings || null,
                        sessionStartedAt: data.sessionStartedAt ?? null,
                        sessionDurationMinutes: data.sessionDurationMinutes ?? null,
                        durationHistory: Array.isArray(data.durationHistory) && data.durationHistory.length > 0
                            ? data.durationHistory
                            : [15, 30, 45, 60],
                        watchHistory: safeLocalHistory,
                        discoveredVideos: safeDiscoveredVideos,
                        searchCache: safeSearchCache,
                        playbackResume: null,
                        lastSyncedAt: new Date().toISOString(),
                        isCloudHydrated: true,
                        isSyncing: false
                    });
                    return get().fetchWatchHistory(cloudCurrentProfile.id, accountId);
                } catch (e) {
                    if (get().accountId === accountId) set({ isSyncing: false });
                    return false;
                }
            },

            fetchWatchHistory: async (profileId?: string, expectedAccountId?: string) => {
                const accountId = expectedAccountId || get().accountId;
                const targetProfileId = profileId || get().currentProfile?.id || DEFAULT_PROFILE.id;
                if (!accountId || get().accountId !== accountId) return false;

                try {
                    const historyResponse = await fetch(
                        `/api/watch-history?view=progress&limit=200&profileId=${encodeURIComponent(targetProfileId)}`,
                        { method: 'GET', credentials: 'include' }
                    );
                    if (!historyResponse.ok || get().accountId !== accountId) return false;

                    const historyData = await historyResponse.json();
                    const cloudHistory = mapCloudWatchHistory(
                        historyData.progress || [],
                        targetProfileId
                    );
                    const state = get();
                    const localForProfile = state.watchHistory.filter(entry => entry.profileId === targetProfileId);
                    const merged = mergeWatchHistoryEntries(localForProfile, cloudHistory)
                        .filter(entry => !isBlockedEntry(entry, state.denylist));
                    const resume = merged.find(entry => (entry.lastPositionSeconds || 0) > 3) || null;

                    if (get().accountId === accountId && get().currentProfile?.id === targetProfileId) {
                        set({
                            watchHistory: merged,
                            playbackResume: resume
                                ? { videoId: resume.videoId, seconds: resume.lastPositionSeconds || 0, updatedAt: resume.watchedAt }
                                : null
                        });
                    }
                    return true;
                } catch (e) {
                    return false;
                }
            },

            migrateExistingWatchState: async (expectedAccountId?: string) => {
                const accountId = expectedAccountId || get().accountId;
                if (!accountId || get().accountId !== accountId) return false;

                const state = get();
                const entries = mergeWatchHistoryEntries(state.watchHistory, []);
                const resume = state.playbackResume;
                if (resume) {
                    const resumeProfileId = state.currentProfile?.id || DEFAULT_PROFILE.id;
                    const resumeIndex = entries.findIndex(entry => entry.videoId === resume.videoId && entry.profileId === resumeProfileId);
                    if (resumeIndex >= 0) {
                        entries[resumeIndex] = {
                            ...entries[resumeIndex],
                            lastPositionSeconds: Math.max(entries[resumeIndex].lastPositionSeconds || 0, resume.seconds),
                            watchedAt: resume.updatedAt
                        };
                    } else {
                        entries.push({
                        profileId: state.currentProfile?.id || DEFAULT_PROFILE.id,
                        videoId: resume.videoId,
                        videoTitle: 'Unknown video',
                        channelTitle: '',
                        thumbnailUrl: '',
                        watchedAt: resume.updatedAt,
                        watchedDurationSeconds: 0,
                        totalDurationSeconds: 0,
                        lastPositionSeconds: resume.seconds
                        });
                    }
                }

                for (const entry of entries) {
                    const key = `${entry.profileId}:${entry.videoId}`;
                    if (get().migratedWatchKeys.includes(key)) continue;
                    try {
                        const response = await fetch('/api/watch-history', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({
                                mode: 'progress_seed',
                                profileId: entry.profileId,
                                videoId: entry.videoId,
                                videoTitle: entry.videoTitle,
                                channelId: entry.channelId || '',
                                channelTitle: entry.channelTitle,
                                thumbnailUrl: entry.thumbnailUrl,
                                watchedDurationSeconds: normalizeSeconds(entry.watchedDurationSeconds),
                                totalDurationSeconds: normalizeSeconds(entry.totalDurationSeconds),
                                lastPositionSeconds: normalizeSeconds(entry.lastPositionSeconds)
                            })
                        });
                        if (!response.ok || get().accountId !== accountId) return false;
                        set(current => ({ migratedWatchKeys: [...current.migratedWatchKeys, key] }));
                    } catch (e) {
                        return false;
                    }
                }
                return true;
            },


            startSession: (limitMinutes: number) => {
                const state = get();
                if (!state.currentProfile) {
                    set({ currentProfile: state.profiles[0] || DEFAULT_PROFILE });
                }

                // Add to duration history (LIFO, max 10, no duplicates)
                const history = state.durationHistory.filter(d => d !== limitMinutes);
                const newHistory = [limitMinutes, ...history].slice(0, 10);

                set({
                    sessionStartedAt: new Date().toISOString(),
                    sessionDurationMinutes: limitMinutes,
                    durationHistory: newHistory
                });
                queueMicrotask(() => { void get().syncToCloud(); });
            },

            resetSession: () => {
                // Parent-only: completely clear session
                set({
                    sessionStartedAt: null,
                    sessionDurationMinutes: null
                });
                queueMicrotask(() => { void get().syncToCloud(); });
            },

            extendSession: (additionalMinutes: number) => {
                const state = get();
                if (!state.sessionDurationMinutes) return;

                if (get().getRemainingSeconds() <= 0) {
                    set({
                        sessionStartedAt: new Date().toISOString(),
                        sessionDurationMinutes: additionalMinutes
                    });
                    queueMicrotask(() => { void get().syncToCloud(); });
                    return;
                }

                set({
                    sessionDurationMinutes: state.sessionDurationMinutes + additionalMinutes
                });
                queueMicrotask(() => { void get().syncToCloud(); });
            },

            unlockWithPin: async (pin: string) => {
                const state = get();
                if (!state.parentSettings) {
                    // First time - create PIN
                    const salt = crypto.randomUUID();
                    const hash = await hashPin(pin, salt);
                    set({
                        parentSettings: {
                            pinHash: hash,
                            pinSalt: salt,
                            allowlist: [],
                            denylist: [],
                            profiles: state.profiles,
                            defaultProfileId: 'default',
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString()
                        }
                    });
                    queueMicrotask(() => { void get().syncToCloud(); });
                    return true;
                }
                const hash = await hashPin(pin, state.parentSettings.pinSalt);
                return hash === state.parentSettings.pinHash;
            },

            toggleFullscreen: () => {
                if (!document.fullscreenElement) {
                    document.documentElement.requestFullscreen().catch(() => { });
                    set({ isFullscreen: true });
                } else {
                    document.exitFullscreen().catch(() => { });
                    set({ isFullscreen: false });
                }
            },

            addToWatchHistory: (entry: Omit<WatchHistoryEntry, 'watchedAt'>) => {
                const watchedAt = new Date().toISOString();
                const profileId = entry.profileId || get().currentProfile?.id || DEFAULT_PROFILE.id;
                const watchedDurationSeconds = Math.max(0, Math.floor(entry.watchedDurationSeconds || 0));
                const totalDurationSeconds = Math.max(0, Math.floor(entry.totalDurationSeconds || 0));
                const knownVideo = get().discoveredVideos.find(video => video.id === entry.videoId);
                const nextEntry: WatchHistoryEntry = {
                    ...entry,
                    profileId,
                    channelId: entry.channelId || knownVideo?.snippet.channelId,
                    watchedAt,
                    watchedDurationSeconds,
                    totalDurationSeconds
                };

                set((state) => {
                    const existingIndex = state.watchHistory.findIndex(item =>
                        item.videoId === entry.videoId && item.profileId === profileId
                    );

                    if (existingIndex === -1) {
                        return { watchHistory: [nextEntry, ...state.watchHistory].slice(0, WATCH_HISTORY_LIMIT) };
                    }

                    const existingEntry = state.watchHistory[existingIndex];
                    const watchHistory = state.watchHistory.filter((_, index) => index !== existingIndex);

                    return {
                        watchHistory: [{
                            ...existingEntry,
                            ...nextEntry,
                            watchedDurationSeconds: Math.max(existingEntry.watchedDurationSeconds || 0, watchedDurationSeconds),
                            totalDurationSeconds: Math.max(existingEntry.totalDurationSeconds || 0, totalDurationSeconds)
                        }, ...watchHistory].slice(0, WATCH_HISTORY_LIMIT)
                    };
                });
            },

            updateWatchHistoryDuration: (videoId: string, secondsToAdd: number, totalDurationSeconds?: number) => {
                if (!videoId || !Number.isFinite(secondsToAdd) || secondsToAdd <= 0) return;

                const wholeSeconds = Math.max(1, Math.floor(secondsToAdd));
                const wholeTotalDurationSeconds = Math.max(0, Math.floor(totalDurationSeconds || 0));
                const watchedAt = new Date().toISOString();
                const profileId = get().currentProfile?.id || DEFAULT_PROFILE.id;

                set((state) => {
                    const existingIndex = state.watchHistory.findIndex(item =>
                        item.videoId === videoId && item.profileId === profileId
                    );

                    if (existingIndex === -1) return {};

                    const existingEntry = state.watchHistory[existingIndex];
                    const watchHistory = state.watchHistory.filter((_, index) => index !== existingIndex);

                    return {
                        watchHistory: [{
                            ...existingEntry,
                            watchedAt,
                            watchedDurationSeconds: Math.max(0, existingEntry.watchedDurationSeconds || 0) + wholeSeconds,
                            totalDurationSeconds: Math.max(existingEntry.totalDurationSeconds || 0, wholeTotalDurationSeconds)
                        }, ...watchHistory].slice(0, WATCH_HISTORY_LIMIT)
                    };
                });
            },

            addDiscoveredVideos: (videos: Video[]) => {
                if (videos.length === 0) return;

                set((state) => {
                    const videosById = new Map<string, Video>();

                    for (const video of videos) {
                        if (isBlockedVideo(video, state.denylist)) continue;
                        videosById.set(video.id, video);
                    }

                    for (const video of state.discoveredVideos) {
                        if (isBlockedVideo(video, state.denylist)) continue;
                        if (!videosById.has(video.id)) {
                            videosById.set(video.id, video);
                        }
                    }

                    return {
                        discoveredVideos: [...videosById.values()].slice(0, DISCOVERED_VIDEO_LIMIT)
                    };
                });
            },

            cacheSearchResults: (queryKey: string, videos: Video[]) => {
                const state = get();
                const safeVideos = videos.filter(video => !isBlockedVideo(video, state.denylist));
                set({
                    searchCache: {
                        queryKey,
                        videos: safeVideos,
                        cachedAt: new Date().toISOString()
                    }
                });
            },

            clearSearchCache: () => set({ searchCache: null }),

            addToAllowlist: (entry: ListEntry) => {
                console.log('[DEBUG] addToAllowlist called with entry:', entry);
                const state = get();
                if (!state.allowlist.some(e => e.id === entry.id)) {
                    console.log('[DEBUG] Adding to local allowlist');
                    set({ allowlist: [...state.allowlist, entry] });
                } else {
                    console.log('[DEBUG] Item already in local allowlist, skipping local update');
                }
                // Sync to cloud - uses httpOnly session cookie
                // Always sync to ensure cloud is up to date, even if local state has it (idempotent)
                if (!get().accountId || !get().isCloudHydrated) return;
                console.log('[DEBUG] Sending POST to /api/sync with action: add_allow');
                fetch('/api/sync', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ action: 'add_allow', item: entry })
                })
                    .then(res => console.log('[DEBUG] /api/sync response status:', res.status))
                    .catch(err => console.error('[DEBUG] /api/sync error:', err));
            },

            addToDenylist: (entry: ListEntry) => {
                console.log('[DEBUG] addToDenylist called with entry:', entry);
                const state = get();
                if (!state.denylist.some(e => e.id === entry.id)) {
                    console.log('[DEBUG] Adding to local denylist');
                } else {
                    console.log('[DEBUG] Item already in local denylist, skipping local update');
                }
                // Purge every local representation even when the preference was
                // already present (for example, after a delayed cloud revalidation).
                set({
                    denylist: state.denylist.some(e => e.id === entry.id) ? state.denylist : [...state.denylist, entry],
                    discoveredVideos: state.discoveredVideos.filter(video =>
                        entry.type === 'channel' ? video.snippet.channelId !== entry.id : video.id !== entry.id
                    ),
                    searchCache: state.searchCache
                        ? {
                            ...state.searchCache,
                            videos: state.searchCache.videos.filter(video =>
                                entry.type === 'channel' ? video.snippet.channelId !== entry.id : video.id !== entry.id
                            )
                        }
                        : null,
                    watchHistory: state.watchHistory.filter(item =>
                        entry.type === 'channel'
                            ? (item.channelId ? item.channelId !== entry.id : item.channelTitle !== entry.title)
                            : item.videoId !== entry.id
                    )
                });
                // Sync to cloud - uses httpOnly session cookie
                // Always sync to ensure cloud is up to date, even if local state has it (idempotent)
                if (!get().accountId || !get().isCloudHydrated) return;
                console.log('[DEBUG] Sending POST to /api/sync with action: add_deny');
                fetch('/api/sync', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ action: 'add_deny', item: entry })
                })
                    .then(res => console.log('[DEBUG] /api/sync response status:', res.status))
                    .catch(err => console.error('[DEBUG] /api/sync error:', err));
            },

            removeFromAllowlist: (id: string) => {
                set((state) => ({ allowlist: state.allowlist.filter(e => e.id !== id) }));
                // Sync to cloud - uses httpOnly session cookie
                if (!get().accountId || !get().isCloudHydrated) return;
                fetch('/api/sync', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ action: 'remove_allow', item: { id } })
                }).catch(() => { });
            },

            removeFromDenylist: (id: string) => {
                set((state) => ({ denylist: state.denylist.filter(e => e.id !== id) }));
                // Sync to cloud - uses httpOnly session cookie
                if (!get().accountId || !get().isCloudHydrated) return;
                fetch('/api/sync', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ action: 'remove_deny', item: { id } })
                }).catch(() => { });
            },

            savePlaybackPosition: (videoId: string, seconds: number, totalDurationSeconds?: number) => {
                if (!videoId || !Number.isFinite(seconds) || seconds < 0) return;
                const lastPositionSeconds = Math.floor(seconds);
                const wholeTotalDurationSeconds = normalizeSeconds(totalDurationSeconds);
                const updatedAt = new Date().toISOString();

                set({
                    playbackResume: {
                        videoId,
                        seconds: lastPositionSeconds,
                        updatedAt
                    }
                });

                set((state) => ({
                    watchHistory: state.watchHistory.map(entry => {
                        if (entry.videoId !== videoId || entry.profileId !== (state.currentProfile?.id || DEFAULT_PROFILE.id)) return entry;

                        return {
                            ...entry,
                            watchedAt: updatedAt,
                            lastPositionSeconds,
                            totalDurationSeconds: Math.max(entry.totalDurationSeconds || 0, wholeTotalDurationSeconds)
                        };
                    })
                }));
            },

            clearPlaybackPosition: (videoId: string) => {
                const state = get();
                if (state.playbackResume?.videoId === videoId) {
                    set({ playbackResume: null });
                }
            },

            importSettings: (data: SettingsExport) => {
                const state = get();
                if (!state.parentSettings) return;
                set({
                    profiles: data.parentSettings.profiles,
                    allowlist: data.parentSettings.allowlist,
                    denylist: data.parentSettings.denylist,
                    watchHistory: data.watchHistory || state.watchHistory,
                    parentSettings: {
                        ...state.parentSettings,
                        ...data.parentSettings,
                        pinHash: state.parentSettings.pinHash,
                        pinSalt: state.parentSettings.pinSalt,
                        updatedAt: new Date().toISOString()
                    },
                });
                queueMicrotask(() => { void get().syncToCloud(true); });
            },

            // COMPUTED FUNCTIONS - These calculate based on current time

            getRemainingSeconds: () => {
                const state = get();
                if (!state.sessionStartedAt || !state.sessionDurationMinutes) {
                    return 0;
                }
                const startedAt = new Date(state.sessionStartedAt).getTime();
                const durationMs = state.sessionDurationMinutes * 60 * 1000;
                const expiresAt = startedAt + durationMs;
                const now = Date.now();
                const remainingMs = expiresAt - now;
                return Math.max(0, Math.floor(remainingMs / 1000));
            },

            isTimeUp: () => {
                return get().getRemainingSeconds() <= 0;
            },

            hasActiveSession: () => {
                const state = get();
                return state.sessionStartedAt !== null && state.sessionDurationMinutes !== null;
            }
        }),
        {
            name: 'kidstube-storage',
            partialize: (state) => ({
                accountId: state.accountId,
                migratedWatchKeys: state.migratedWatchKeys,
                currentProfile: state.currentProfile,
                profiles: state.profiles,
                sessionStartedAt: state.sessionStartedAt,           // PERSIST
                sessionDurationMinutes: state.sessionDurationMinutes, // PERSIST
                parentSettings: state.parentSettings,
                allowlist: state.allowlist,
                denylist: state.denylist,
                watchHistory: state.watchHistory,
                discoveredVideos: state.discoveredVideos,
                searchCache: state.searchCache,
                durationHistory: state.durationHistory,  // PERSIST
                playbackResume: state.playbackResume,
                lastSyncedAt: state.lastSyncedAt  // PERSIST cloud sync timestamp
            })
        }
    )
);
